export const PERMISSIONS_LEVELS = {
  UNAUTHORIZED: -1,
  BASED: 0,
  VIEW: 1,
  EDIT: 2,
  APP_ADMIN: 3,
}

export const BASE_PERMISSION = 0

// Main permission levels from permanentData
export type MainPermissionLevel = typeof PERMISSIONS_LEVELS.VIEW | typeof PERMISSIONS_LEVELS.EDIT // view | edit

export interface PermissionItem {
  key: string
  name: string
  icon: React.ReactNode
  permission: number
}

export interface PermissionsData {
  [key: string]: number
}

export const PERMISSION_LEVELS = [
  { id: PERMISSIONS_LEVELS.UNAUTHORIZED, name: 'unauthorized', value: PERMISSIONS_LEVELS.UNAUTHORIZED },
  { id: PERMISSIONS_LEVELS.VIEW, name: 'admin_view_option', value: PERMISSIONS_LEVELS.VIEW },
  { id: PERMISSIONS_LEVELS.EDIT, name: 'admin_edit_action', value: PERMISSIONS_LEVELS.EDIT },
  // { id: 3, name: 'Admin', value: 3 },
] as const

export const DEFAULT_PERMISSIONS: PermissionsData = {
  reports: PERMISSIONS_LEVELS.VIEW,
  posts: PERMISSIONS_LEVELS.VIEW,
  comments: PERMISSIONS_LEVELS.VIEW,
  tasks: PERMISSIONS_LEVELS.VIEW,
  pops: PERMISSIONS_LEVELS.VIEW,
  drive: PERMISSIONS_LEVELS.VIEW,
}

// Service keys used in the permissions table
export type ServiceKey = 'reports' | 'posts' | 'comments' | 'tasks' | 'pops' | 'drive'

// Absolute bounds for each service
// - reports: max VIEW (allowed only UNAUTHORIZED or VIEW)
// - drive (Library): UNAUTHORIZED (no access) → VIEW (browse) → EDIT (create/manage)
// - others: min VIEW (UNAUTHORIZED is not allowed)
export const SERVICE_BOUNDS: Record<ServiceKey, { min: number; max: number }> = {
  reports: { min: PERMISSIONS_LEVELS.UNAUTHORIZED, max: PERMISSIONS_LEVELS.VIEW },
  posts: { min: PERMISSIONS_LEVELS.VIEW, max: PERMISSIONS_LEVELS.EDIT },
  comments: { min: PERMISSIONS_LEVELS.VIEW, max: PERMISSIONS_LEVELS.EDIT },
  tasks: { min: PERMISSIONS_LEVELS.VIEW, max: PERMISSIONS_LEVELS.EDIT },
  pops: { min: PERMISSIONS_LEVELS.VIEW, max: PERMISSIONS_LEVELS.EDIT },
  drive: { min: PERMISSIONS_LEVELS.UNAUTHORIZED, max: PERMISSIONS_LEVELS.EDIT },
}

export const getMaxAllowedLevel = (mainPermission: MainPermissionLevel) => {
  if (mainPermission === PERMISSIONS_LEVELS.VIEW) return PERMISSIONS_LEVELS.VIEW // view -> maximum view
  if (mainPermission === PERMISSIONS_LEVELS.EDIT) return PERMISSIONS_LEVELS.APP_ADMIN // edit -> maximum admin
  return -1
}

export const getInheritPermissions = (mainPermission: MainPermissionLevel): PermissionsData => {
  const mainMax = getMaxAllowedLevel(mainPermission)

  // Each service takes its maximum allowed level considering both service bounds and main permission
  const result: PermissionsData = {}
  ;(['reports', 'posts', 'comments', 'tasks', 'pops', 'drive'] as ServiceKey[]).forEach((key) => {
    const { max } = SERVICE_BOUNDS[key]
    const effectiveMax = Math.min(max, mainMax)
    result[key] = effectiveMax
  })

  return result
}

// Helper function to normalize permission value 0 to -1 for UI display
export const normalizePermissionForUI = (permission: number): number => {
  return permission === BASE_PERMISSION ? PERMISSIONS_LEVELS.UNAUTHORIZED : permission
}
